import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Clock,
  Sparkles,
  UtensilsCrossed,
  Waves,
  Wifi,
  ShieldCheck,
  Dumbbell,
  Car,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ResortTemplate, {
  type ResortFeature,
  type ResortGalleryItem,
  type ResortStat,
} from "@/components/ResortTemplate";
import { useAuthState } from "@/hooks/useAuthState";
import {
  DevelopmentService,
  parseDevelopmentAmenities,
  parseDevelopmentPhotos,
  type Development,
} from "@/services/developmentService";
import { resolveAmenityIcon } from "@/lib/amenityIcons";
import { buildResortGalleryPaths } from "@/lib/resortGalleryPaths";
import { getDevelopmentLogoUrl } from "@/config/api";
import { Button } from "@/components/ui/button";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";

const stripHtml = (html?: string): string => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const splitDescription = (raw?: string): string[] => {
  const text = stripHtml(raw);
  if (!text) return [];
  const parts = text
    .split(/\n+|(?<=\.)\s+(?=[A-ZÁÉÍÓÚÂÊÔÃÕÇ])/g)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return [text];
  if (parts.length <= 3) return parts;
  return [parts.slice(0, 2).join(" "), parts.slice(2).join(" ")];
};

const ResortDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const [development, setDevelopment] = useState<Development | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const item = await DevelopmentService.getDevelopmentBySlug(slug);
        setDevelopment(item);
      } catch (error) {
        console.error("Erro ao carregar resort:", error);
        setDevelopment(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const photos = useMemo(
    () => parseDevelopmentPhotos(development?.photos),
    [development?.photos],
  );

  const coverImagePath = useMemo(() => {
    if (!development) return null;
    if (development.cover_image && development.cover_image.trim() !== "") {
      return development.cover_image;
    }
    if (photos[0]) return photos[0];
    return null;
  }, [development, photos]);

  const coverImage = useMemo(() => {
    if (!development) return heroMuroAlto1;
    if (coverImagePath) return getDevelopmentLogoUrl(coverImagePath);
    if (development.logo) return getDevelopmentLogoUrl(development.logo);
    return heroMuroAlto1;
  }, [development, coverImagePath]);

  /** Galeria: `photos` + capa; se ambos vazios na API, usa `logo` (caso comum quando só a logo foi salva). */
  const adminGalleryPaths = useMemo(
    () =>
      buildResortGalleryPaths({
        photoPaths: photos,
        coverImagePath,
        logoPath: development?.logo,
      }),
    [photos, coverImagePath, development?.logo],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar
          isAuthenticated={isAuthenticated}
          isOwner={userType === "owner"}
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          userType={userType}
        />
        <div className="container mx-auto px-4 py-24 text-center text-muted-foreground">
          Carregando resort...
        </div>
        <Footer />
      </div>
    );
  }

  if (!development) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar
          isAuthenticated={isAuthenticated}
          isOwner={userType === "owner"}
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          userType={userType}
        />
        <div className="container mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold">Resort não encontrado</h1>
          <p className="mt-3 text-muted-foreground">Este resort não está disponível.</p>
          <Button asChild className="mt-6">
            <Link to="/">Voltar para Home</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const cityState = [development.city, development.state].filter(Boolean).join(" - ") ||
    "Brasil";

  const adminGalleryUrls = adminGalleryPaths.map((path) => getDevelopmentLogoUrl(path));
  const hasAdminGallery = adminGalleryUrls.length > 0;

  const coverKey = (coverImagePath || "").trim();
  const firstAboutPath = adminGalleryPaths.find((p) => p.trim() !== coverKey);
  const aboutImage = firstAboutPath
    ? getDevelopmentLogoUrl(firstAboutPath)
    : coverImage !== heroMuroAlto1
      ? coverImage
      : hasAdminGallery
        ? adminGalleryUrls[0]
        : development.logo
          ? getDevelopmentLogoUrl(development.logo)
          : coverImage;

  /** Só imagens cadastradas no admin — nunca fotos genéricas de outro resort. */
  const galleryItems: ResortGalleryItem[] = hasAdminGallery
    ? adminGalleryUrls.map((src, index) => ({
        src,
        alt: `${development.name} — foto ${index + 1}`,
        span: index === 0 ? "big" : index === 3 ? "wide" : undefined,
      }))
    : [];

  const aboutParagraphs = splitDescription(development.description);
  const fallbackAbout = [
    `Conheça o ${development.name}, um empreendimento exclusivo da LiveStay${
      cityState ? ` em ${cityState}` : ""
    }.`,
    "Estrutura completa de lazer, acomodações sofisticadas e atendimento personalizado para garantir uma experiência inesquecível.",
  ];

  const stats: ResortStat[] = [
    {
      value: development.check_in_time || "15h",
      label: "Check-in",
    },
    {
      value: development.check_out_time || "12h",
      label: "Check-out",
    },
    {
      value: "24/7",
      label: "Concierge",
    },
  ];

  // Amenidades cadastradas no admin → ResortFeature[] com ícone resolvido
  const developmentAmenities = parseDevelopmentAmenities(development.amenities);
  const amenityFeatures: ResortFeature[] = developmentAmenities.map((amenity) => ({
    icon: resolveAmenityIcon(amenity.icon).Icon,
    title: amenity.name,
    description: amenity.description ?? "",
  }));

  // Fallback (caso o empreendimento ainda não tenha amenidades cadastradas)
  const fallbackFeatures: ResortFeature[] = [
    {
      icon: Clock,
      title: "Atendimento dedicado",
      description: development.check_in_time
        ? `Check-in às ${development.check_in_time} e check-out às ${
            development.check_out_time || "12h"
          }, com concierge e equipe disponível para apoiar sua estadia.`
        : "Recepção e concierge disponíveis para apoiar sua estadia em todos os momentos.",
    },
    {
      icon: Waves,
      title: "Áreas de lazer",
      description: "Piscinas, áreas de descanso e ambientes pensados para relaxar e curtir.",
    },
    {
      icon: UtensilsCrossed,
      title: "Gastronomia",
      description: "Restaurantes, bares e opções culinárias para todos os momentos do dia.",
    },
    {
      icon: Wifi,
      title: "Wi-Fi de alta velocidade",
      description: "Internet rápida e gratuita disponível em todas as áreas do empreendimento.",
    },
    {
      icon: Sparkles,
      title: "Spa & Bem-estar",
      description: "Tratamentos exclusivos e ambientes voltados ao relaxamento e bem-estar.",
    },
    {
      icon: Dumbbell,
      title: "Esportes & Atividades",
      description: "Academia equipada, quadras esportivas e atividades para toda a família.",
    },
    {
      icon: Car,
      title: "Estacionamento",
      description: "Vagas privativas e fácil acesso para hóspedes e visitantes.",
    },
    {
      icon: ShieldCheck,
      title: "Segurança 24h",
      description: "Acesso controlado, monitoramento e equipe treinada para sua tranquilidade.",
    },
  ];

  const featureItems = amenityFeatures.length > 0 ? amenityFeatures : fallbackFeatures;

  const directionsTarget = encodeURIComponent(
    [development.address, cityState].filter(Boolean).join(", ") || development.name,
  );
  const mapEmbedUrl = `https://www.google.com/maps?q=${directionsTarget}&hl=pt-BR&output=embed`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${directionsTarget}`;

  return (
    <>
      <ResortTemplate
        name={development.name}
        location={cityState}
        highlightLabel="Empreendimento LiveStay"
        heroImage={coverImage}
        heroDescription={
          stripHtml(development.description).slice(0, 220) ||
          `${development.name} é um empreendimento exclusivo, com estrutura de lazer e atendimento personalizado.`
        }
        primaryCtaTo={`/busca?empreendimento=${encodeURIComponent(development.name)}`}
        about={{
          eyebrow: "Sobre o empreendimento",
          title: `Conheça o ${development.name}`,
          paragraphs: aboutParagraphs.length > 0 ? aboutParagraphs : fallbackAbout,
          image: aboutImage,
          imageAlt: `${development.name} — vista do empreendimento`,
          stats,
        }}
        gallery={
          galleryItems.length > 0
            ? {
                title: "Galeria do empreendimento",
                description: "Fotos cadastradas para este empreendimento.",
                items: galleryItems,
              }
            : undefined
        }
        features={{
          title: "Comodidades pensadas para a sua estadia",
          description:
            "Comodidades pensadas para que sua estadia seja memorável do check-in ao check-out.",
          items: featureItems,
        }}
        location_section={{
          title: `Visite o ${development.name}`,
          description:
            development.address
              ? `Estamos em ${development.address}${cityState ? ` — ${cityState}` : ""}.`
              : `Empreendimento em ${cityState}.`,
          contact: {
            address: development.address || "Endereço sob consulta com a recepção",
            cityState,
            phone: development.phone,
            travelInfo: development.address
              ? `${development.address}${cityState ? `, ${cityState}` : ""}`
              : undefined,
            mapEmbedUrl,
            directionsUrl,
          },
        }}
        developmentId={development.id}
      />

    </>
  );
};

export default ResortDetails;
